import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import { AppShell } from '@/components/layout/app-shell';
import { ErrorBoundary } from '@/components/layout/error-boundary';
import { ProtectedRoute } from '@/components/layout/protected-route';
import { RoleGate } from '@/components/layout/role-gate';
import { Toaster } from '@/components/ui/toaster';
import { AuthCallbackPage } from '@/features/auth/auth-callback-page';
import { LoginPage } from '@/features/auth/login-page';
import { CheckInsPage } from '@/features/check-ins/check-ins-page';
import { TrainingHubPage } from '@/features/dashboard/training-hub-page';
import { GuidesPage } from '@/features/guides/guides-page';
import { ProgressPage } from '@/features/progress/progress-page';
import { CertificatePage } from '@/features/training/certificate-page';
import { ModuleListPage } from '@/features/training/module-list-page';
import { ScenarioPlayerPage } from '@/features/training/scenario-player-page';
import { initializeApiClient } from '@/lib/api-client';

initializeApiClient();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000,
      retry: 2,
      refetchOnWindowFocus: false,
    },
  },
});

const router = createBrowserRouter([
  {
    path: '/login',
    element: <ErrorBoundary><LoginPage /></ErrorBoundary>,
  },
  {
    path: '/auth/callback',
    element: <AuthCallbackPage />,
  },
  {
    element: (
      <ErrorBoundary>
        <ProtectedRoute>
          <RoleGate>
            <AppShell />
          </RoleGate>
        </ProtectedRoute>
      </ErrorBoundary>
    ),
    children: [
      { index: true, element: <TrainingHubPage /> },
      { path: 'training', element: <ModuleListPage /> },
      { path: 'training/modules/:moduleId', element: <ScenarioPlayerPage /> },
      { path: 'certificate', element: <CertificatePage /> },
      { path: 'guides', element: <GuidesPage /> },
      { path: 'check-ins', element: <CheckInsPage /> },
      { path: 'progress', element: <ProgressPage /> },
    ],
  },
], { basename: '/' });

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
      <Toaster />
    </QueryClientProvider>
  );
}
